import 'react-datepicker/dist/react-datepicker.css';
import React, { forwardRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { captureException } from '@sentry/react';
import type { GetStaticProps } from 'next';
import Router from 'next/router';
import { useInView } from 'react-intersection-observer';
import { faArrowUpFromBracket } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { ERROR_TYPE, FEATURES, PLAYLIST_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { PlaylistChartCard, PlaylistChartCardSkeleton } from '@components/Charts/PlaylistChartCard';
import { ChartTop } from '@components/Charts/Top';
import { Header, MainContainer, PageWrapper, ShareIcons } from '@components/Charts/shared';
import { Seo } from '@components/Seo';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { ShareModalViral } from '@components/modals/ShareModal';
import { ModalContext, ModalType } from '@context/modal';
import { gql, PlaylistChartQueryDocument, useInfiniteQuery } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { trackClickEvent } from '@lib/analytics';
import { DEFAULT_TOP_CHARTS_PATHNAME } from '@pages-shared/topCharts';
import { handleContentChange, handleTypeChange } from '@pages-shared/viralCharts';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY, NAV_LINKS } from '@utils/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  query PlaylistChartQuery($pagination: PlaylistChartRankCursorConnectionArgs!) {
    currentPlaylistChart {
      id
      lastDayOfChartInclusive
      chartRanks(pagination: $pagination) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            ...PlaylistChartCard
          }
        }
      }
    }
  }
`);

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
    // rebuild every 5 minutes
    revalidate: 300,
  };
};

export default function ViralChartsPlaylistsPage() {
  const logError = useLogError();
  const { openModal } = ModalContext.useContainer();

  useEffect(() => {
    // Prefetch the two paths are immediately accessible
    Router.prefetch(DEFAULT_TOP_CHARTS_PATHNAME).catch(err => {
      captureException(err, {
        level: 'warning',
      });
    });
    Router.prefetch(NAV_LINKS.ViralChartsSounds).catch(err => {
      captureException(err, {
        level: 'warning',
      });
    });
  }, []);

  const {
    orderedList: charts,
    loadMoreNextPage,
    hasNextPage = false,
    isFetching,
    isLoading,
    error,
  } = useInfiniteQuery(PlaylistChartQueryDocument, {
    getNextPageParam({ data: { currentPlaylistChart } }) {
      return (
        currentPlaylistChart?.chartRanks.pageInfo.hasNextPage && {
          after: currentPlaylistChart.chartRanks.pageInfo.endCursor,
        }
      );
    },
    filterQueryKey: {},
    variables({ pageParam }) {
      return {
        pagination: {
          first: 14,
          after: pageParam?.after ?? null,
        },
      };
    },
    list({ currentPlaylistChart }) {
      return currentPlaylistChart?.chartRanks.edges.map(v => v.node);
    },
    uniq(entity) {
      return entity.id;
    },
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });

  const [bottomRef, isAtBottom] = useInView({
    threshold: 0.25,
  });

  useEffect(() => {
    if (isAtBottom && hasNextPage) {
      loadMoreNextPage();
    }
  }, [hasNextPage, isAtBottom, loadMoreNextPage]);

  useEffect(() => {
    if (!error) return;
    logError({
      error,
      feature: FEATURES.PLAYLISTS,
      isArtistOnlyPage: false,
      message: 'Error loading data for playlist viral charts',
      action: PLAYLIST_ERROR_ACTIONS.PLAYLIST_VIRAL_CHARTS_FETCH_ERROR,
      errorType: ERROR_TYPE.FETCH_GQL_ERROR,
      level: 'fatal',
    });
  }, [error, logError]);

  return (
    <PageWrapper>
      <Seo title="Viral Charts | Sound.xyz" description="What's trending this week on Sound.xyz" />
      <MainContainer>
        <Header>
          <ChartTop
            chartType="viral"
            contentType="playlists"
            onChartContentSelection={handleContentChange}
            onChartTypeSelection={handleTypeChange}
            icons={
              <ShareIcons>
                <IconButton
                  icon={<FontAwesomeIcon icon={faArrowUpFromBracket} size="lg" />}
                  onClick={() => {
                    trackClickEvent({
                      event: 'Share Viral Sounds Clicked',
                      properties: {
                        sourceComponent: 'Chart',
                      },
                    });
                    openModal(ModalType.SHARE, {
                      body: <ShareModalViral />,
                    });
                  }}
                />
              </ShareIcons>
            }
          />
        </Header>
        {charts.map(chart => (
          <PlaylistChartCard key={chart.id} chart={chart} />
        ))}
        <BottomRow show={isFetching} isLoading={isLoading} ref={bottomRef} />
      </MainContainer>
    </PageWrapper>
  );
}

export const BottomRow = forwardRef<
  HTMLDivElement,
  {
    show: boolean;
    isLoading: boolean;
  }
>(function BottomRow({ show, isLoading }, ref) {
  const rows = isLoading ? 7 : 1;
  return (
    <div className="w-full">
      {/* We can use a full skeleton on initial load */}
      {/* Avoid infinite loading state by using a skeleton that doesn't overflow the viewport */}
      {show && <PlaylistChartCardSkeleton rows={rows} />}
      <div ref={ref} />
    </div>
  );
});
